import {
  CreateTaskApiResponse,
  CreateTaskRequestBody,
  UpdateTaskApiRequest,
  UpdateTaskApiResponse,
  UpdateOccurrenceApiRequest,
  DeleteOccurrenceApiRequest,
} from '@momentum/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { issueService } from '../services';

// Query keys for goals
export const tasksKeys = {
  all: ['tasks'] as const,
  lists: () => [...tasksKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...tasksKeys.lists(), { filters }] as const,
  details: () => [...tasksKeys.all, 'detail'] as const,
  detail: (id: string) => [...tasksKeys.details(), id] as const,
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTaskRequestBody): Promise<CreateTaskApiResponse> =>
      issueService.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      // Invalidate user-assigned-goals for one-on-one review
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Omit<UpdateTaskApiRequest, '_id'>;
    }): Promise<UpdateTaskApiResponse> => issueService.updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
    },
  });
};

export const useUpdateOccurrenceHistory = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateTaskApiResponse,
    Error,
    { taskId: string; data: UpdateOccurrenceApiRequest }
  >({
    mutationFn: ({ taskId, data }) =>
      issueService.updateOccurrenceHistory(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
    },
  });
};

export const useDeleteOccurrence = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateTaskApiResponse,
    Error,
    { taskId: string; data: DeleteOccurrenceApiRequest }
  >({
    mutationFn: ({ taskId, data }) =>
      issueService.deleteOccurrence(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
    },
  });
};
